import { useState } from 'react';
import { useAuth } from '../lib/auth.jsx';
import { useData } from '../lib/data.jsx';
import { useUI } from '../lib/ui.jsx';
import { rpc } from '../lib/supabase.js';
import { B } from '../lib/util.js';
import { CreditSheet } from '../lib/print.jsx';

export default function Credits() {
  const { vatRate, settings, me } = useAuth();
  const { credits, bySku, refresh } = useData();
  const { toast, openSheet, closeSheet } = useUI();
  const [busy, setBusy] = useState(false);
  const creditDays = Number(settings.credit_days ?? 15);

  const active = credits.filter(c => c.status === 'active');

  const useCredit = (c) => {
    const cat = bySku(c.sku);
    const price = Number(cat?.retail ?? c.current_retail ?? 0);
    if (!price) { toast('ไม่พบราคาสินค้า SKU นี้ในระบบ'); return; }
    const applied = Math.min(Number(c.amount), price);   // เกินราคา = ตัดเท่าราคา ไม่ทอน
    const forfeited = Number(c.amount) - applied;
    const net = price - applied;                          // VAT คิดจากยอดหลังหักส่วนลด
    const exVat = Math.round(net / (1 + vatRate));
    const vat = net - exVat;

    const confirm = async () => {
      const saleTxn = (document.getElementById('rpSaleTxn')?.value || '').trim();
      if (!saleTxn) { toast('ใส่เลขบิลขายก่อน — เครดิตต้องผูกกับบิลที่ใช้จริง'); return; }
      setBusy(true);
      try {
        await rpc('rp_redeem_credit', { p_credit: c.id, p_price: price, p_sale_txn: saleTxn });
        closeSheet();
        toast(`ใช้เครดิต ${B(applied)} ฿ เป็นส่วนลดแล้ว`);
        await refresh();
      } catch (e) { toast(e.message); }
      setBusy(false);
    };

    openSheet(
      <div>
        <CreditSheet credit={c} price={price} applied={applied} forfeited={forfeited}
          net={net} exVat={exVat} vat={vat} branchName={me.branch_name || 'สาขาหลัก'} />
        <div className="noprint" style={{ marginTop: 22 }}>
          <label className="mini" style={{ display: 'block', marginBottom: 4 }}>เลขบิลขาย (จาก POS/SaleCore) ที่นำเครดิตไปใช้ — บังคับ</label>
          <input id="rpSaleTxn" placeholder="เช่น INV-680807-001" autoComplete="off"
            style={{ width: 260, border: 0, borderBottom: '1px solid var(--line2)', background: 'transparent', padding: '8px 2px', fontFamily: 'var(--mono)', fontSize: 14 }} />
          <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
            <button className="btn" disabled={busy} onClick={confirm}>ยืนยันใช้เครดิตเป็นส่วนลด</button>
            <button className="btn ghost" onClick={closeSheet}>ปิด</button>
          </div>
        </div>
      </div>
    );
  };

  return (<>
    <h2>เครดิตเช่า→ซื้อ</h2>
    <div className="hint">ค่าเช่าทริปแรกใช้เป็นส่วนลดซื้อสินค้ารุ่นเดียวกัน ภายใน {creditDays} วันหลังคืนของ · ใช้ครั้งเดียวจบ ไม่ทอน ไม่ยกยอด</div>
    <div>
      {active.map(c => {
        const hot = c.days_left <= 5;
        const pct = Math.max(0, Math.min(100, Math.round(c.days_left / creditDays * 100)));
        return (
          <div className="cred" key={c.id}>
            <div>
              <div style={{ fontWeight: 600 }}>{c.customer_name}</div>
              <div className="mono" style={{ fontSize: 11, color: 'var(--muted)' }}>{c.name_snapshot} · {c.sku}</div>
            </div>
            <div className="amt">{B(c.amount)} ฿</div>
            <div className={'days' + (hot ? ' hot' : '')}>เหลือ {c.days_left} วัน</div>
            <div className="bar"><i className={hot ? 'hot' : ''} style={{ width: pct + '%' }} /></div>
            <button className="btn sm" onClick={() => useCredit(c)}>ใช้เป็นส่วนลด</button>
          </div>
        );
      })}
      {!active.length && <div className="empty">ยังไม่มีเครดิตค้าง</div>}
    </div>
    {credits.some(c => c.status !== 'active') && (
      <div style={{ marginTop: 26 }}>
        <h2>ประวัติเครดิต</h2>
        <table>
          <thead><tr><th>ลูกค้า</th><th>SKU</th><th className="r">ออก</th><th className="r">ใช้จริง</th><th>สถานะ</th><th>บิลอ้างอิง</th></tr></thead>
          <tbody>
            {credits.filter(c => c.status !== 'active').map(c => (
              <tr key={c.id}>
                <td>{c.customer_name}</td>
                <td className="mono">{c.sku}</td>
                <td className="r mono">{B(c.amount)}</td>
                <td className="r mono">{c.redeemed_amount != null ? B(c.redeemed_amount) : '—'}</td>
                <td>{c.status === 'redeemed' ? <span className="chip c-out">ใช้แล้ว</span> :
                  c.status === 'expired' ? <span className="chip c-fix">หมดอายุ</span> :
                    <span className="chip c-fix">ยกเลิก</span>}</td>
                <td className="mono">{c.redeem_note || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </>);
}
